import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './AuthContext';

const TUTORIAL_DONE_KEY = 'tutorial_done_marketplace';

export interface TutorialStep {
  id: string;
  target?: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  actionLabel?: string;
  scrollTo?: boolean;
  highlight?: boolean;
  action?: string;
  onEnter?: (actions: TutorialActions) => void;
  remeasureDelay?: number;
  waitForAction?: boolean; // if true: no Next btn, blocker lets clicks through, auto-advance when action detected
  pollFor?: string;        // CSS selector to poll for existence/change to auto-advance
  pollForExpanded?: boolean; // special: advance when [data-villager="Ace"] .card-expanded appears
  pollForCart?: boolean;  // advance when basket contains Ace
  pollForCartOpen?: boolean; // advance when cart pill is clicked (checkout visible)
  pollForCheckoutSent?: boolean; // advance after send request clicked
}

export interface TutorialActions {
  expandAce: () => void;
  collapseAce: () => void;
  addAceToCart: () => void;
  openCart: () => void;
  openCheckout: () => void;
  getBasket: () => string[];
  isCheckoutOpen: () => boolean;
  isCartOpen: () => boolean;
}

const WEB_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    placement: 'center',
    title: '👋 Welcome to Dreamie Store',
    body: "Dreamie Store is an Animal Crossing villager trading platform. Browse hundreds of villagers, request trades, and manage your collection. Tap anywhere to continue.",
    actionLabel: "Let's go →",
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'sidebar',
    target: '.sidebar',
    placement: 'right',
    title: '🗂️ Your Sidebar',
    body: "Your control panel — search, filters, and navigation all live here. Tap anywhere to continue.",
    highlight: true,
    scrollTo: false,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'search',
    target: '.sidebar-search-wrap',
    placement: 'right',
    title: '🔍 Search',
    body: "Type any villager name to instantly filter the grid. 'Mar' finds Marshal, Marina, and more. Tap anywhere to continue.",
    highlight: true,
  },
  {
    id: 'personality-guide',
    target: '.guide-grid-sidebar',
    placement: 'right',
    title: '✨ Personality Guide',
    body: "Every villager has a personality badge:\n💪 Jock  😴 Lazy  😤 Cranky  😏 Smug\n🌸 Normal  💅 Snooty  🌶️ Uchi  ⚡ Peppy\n\nTap anywhere to continue.",
    highlight: true,
  },
  {
    id: 'filters',
    target: '.sidebar-inner',
    placement: 'right',
    title: '⚙️ Filters',
    body: "Gender, Personality and Species filters stack — find 'Female Peppy Bears' in two clicks. Tap anywhere to continue.",
    highlight: true,
  },
  {
    id: 'sidebar-nav',
    target: '.sidebar-nav',
    placement: 'right',
    title: '🧭 Navigation',
    body: "🛒 Marketplace · ⇄ Trades · 👤 Profile · 💬 Feedback\n\nTap anywhere to continue.",
    highlight: true,
  },
  {
    id: 'podium',
    target: '.villager-grid-horizontal > div:first-child',
    placement: 'bottom',
    title: '🏆 Top Traded Podium',
    body: "The top 3 most-traded villagers in the last 24 hours. Gold, silver, bronze. Tap anywhere to continue.",
    highlight: true,
    scrollTo: true,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'ace-card',
    target: '[data-villager="Ace"]',
    placement: 'right',
    title: '🐦 Meet Ace',
    body: "Species icon, name, personality badge, gender colour — blue for male, pink for female. Tap anywhere to continue.",
    highlight: true,
    scrollTo: true,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'card-buttons',
    target: '[data-villager="Ace"] .card-top-actions',
    placement: 'top',
    title: '🎮 Card Buttons',
    body: "✓ Owned  ♥ Favourite  ⭐ Dreamies  → Add to Cart\n\nTap anywhere to continue.",
    highlight: true,
    scrollTo: true,
  },
  {
    id: 'expand-card',
    target: '[data-villager="Ace"]',
    placement: 'right',
    title: '🔍 Expand Ace\'s Card',
    body: "Click Ace's card to expand it — see full details like species, personality and popularity. Go ahead and click it now!",
    highlight: true,
    scrollTo: true,
    waitForAction: true,
    pollForExpanded: true,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'expand-card-show',
    target: '[data-villager="Ace"] .card-expanded',
    placement: 'right',
    title: '🔍 Expanded Details',
    body: "Species, personality, gender and recent popularity all here. Tap anywhere to continue.",
    highlight: true,
    scrollTo: true,
    remeasureDelay: 200,
  },
  {
    id: 'alphabet',
    target: '.alphabet-nav-top',
    placement: 'bottom',
    title: '🔤 A–Z Quick Jump',
    body: "Jump straight to any letter. Press M now to jump to Marshal!",
    highlight: true,
    scrollTo: true,
    waitForAction: true,
    pollFor: '[data-letter="M"].active, #letter-M, .alphabet-letter.active',
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'back-to-ace',
    target: '[data-villager="Ace"]',
    placement: 'right',
    title: '↩️ Back to Ace',
    body: "Great! Now let's add Ace to your cart. We've jumped back to A — tap anywhere to continue.",
    highlight: true,
    remeasureDelay: 700,
    onEnter: (a) => {
      a.collapseAce();
      // Click the A button in the alphabet nav to jump back
      setTimeout(() => {
        const aBtn = Array.from(document.querySelectorAll('.alphabet-btn-top'))
          .find(el => el.textContent?.trim() === 'A') as HTMLElement | undefined;
        if (aBtn) aBtn.click();
      }, 100);
    },
  },
  {
    id: 'cart-add',
    target: '[data-villager="Ace"] .cart-arrow-btn',
    placement: 'top',
    title: '🛒 Add Ace to Cart',
    body: "Press the → button on Ace's card to add them to your cart. Try it now!",
    highlight: true,
    waitForAction: true,
    pollForCart: true,
    onEnter: (a) => {
      a.collapseAce();
      setTimeout(() => {
        const aBtn = Array.from(document.querySelectorAll('.alphabet-btn-top'))
          .find(el => el.textContent?.trim() === 'A') as HTMLElement | undefined;
        if (aBtn) aBtn.click();
      }, 100);
    },
  },
  {
    id: 'cart-pill',
    target: '.cart-pill',
    placement: 'top',
    title: '🛒 Open Your Cart',
    body: "Ace is in your cart! Click the cart pill to open it.",
    highlight: true,
    waitForAction: true,
    pollForCartOpen: true,
  },
  {
    id: 'checkout-plot',
    target: '.checkout-modal',
    placement: 'right',
    title: '💸 Checkout — Tick the Plot Box',
    body: "Tick the \"I have an available plot\" checkbox for Ace, then click Send Request. Go ahead!",
    highlight: true,
    waitForAction: true,
    pollForCheckoutSent: true,
    onEnter: (a) => { a.openCheckout(); },
    remeasureDelay: 300,
  },
  {
    id: 'done',
    placement: 'center',
    title: '🎉 You\'re all set!',
    body: "That's everything! Your trade request was sent and will be cleaned up automatically. Head to ⇄ Trades to manage your requests. Happy trading! 🏝️",
    actionLabel: 'Start exploring →',
  },
];

const MOBILE_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    placement: 'center',
    title: '👋 Welcome to Dreamie Store',
    body: "Browse villagers, request trades, and manage your collection — all from your phone! Tap anywhere to continue.",
    actionLabel: "Let's go →",
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'mobile-pill',
    target: '.mobnav-pill-wrap',
    placement: 'bottom',
    title: '☰ Menu Pill',
    body: "Your navigation hub — pages, filters, settings. Tap anywhere to continue.",
    highlight: true,
  },
  {
    id: 'mobile-filters',
    placement: 'center',
    title: '⚙️ Filters',
    body: "Gender, Personality and Species filters inside the menu. Filters cascade — only options with results are shown. Tap anywhere to continue.",
  },
  {
    id: 'podium',
    target: '.villager-grid-horizontal > div:first-child',
    placement: 'bottom',
    title: '🏆 Top Traded Podium',
    body: "Top 3 most-traded villagers in the last 24 hours. Tap anywhere to continue.",
    highlight: true,
    scrollTo: true,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'ace-card',
    target: '[data-villager="Ace"]',
    placement: 'bottom',
    title: '🐦 Meet Ace',
    body: "Species icon, name, personality badge, gender colour. Tap anywhere to continue.",
    highlight: true,
    scrollTo: true,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'card-buttons',
    target: '[data-villager="Ace"] .card-top-actions',
    placement: 'top',
    title: '🎮 Card Buttons',
    body: "✓ Owned  ♥ Favourite  ⭐ Dreamies  → Cart\n\nTap anywhere to continue.",
    highlight: true,
    scrollTo: true,
  },
  {
    id: 'expand-card',
    target: '[data-villager="Ace"]',
    placement: 'bottom',
    title: '🔍 Expand Ace\'s Card',
    body: "Tap Ace's card to expand it and see full details. Go ahead!",
    highlight: true,
    scrollTo: true,
    waitForAction: true,
    pollForExpanded: true,
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'expand-card-show',
    target: '[data-villager="Ace"] .card-expanded',
    placement: 'bottom',
    title: '🔍 Expanded Details',
    body: "Species, personality, gender and popularity. Tap anywhere to continue.",
    highlight: true,
    scrollTo: true,
    remeasureDelay: 200,
  },
  {
    id: 'alphabet',
    target: '.alphabet-nav-top',
    placement: 'bottom',
    title: '🔤 A–Z Quick Jump',
    body: "Jump to any letter instantly. Tap M now!",
    highlight: true,
    scrollTo: true,
    waitForAction: true,
    pollFor: '[data-letter="M"].active, #letter-M, .alphabet-letter.active',
    onEnter: (a) => { a.collapseAce(); },
  },
  {
    id: 'back-to-ace',
    target: '[data-villager="Ace"]',
    placement: 'bottom',
    title: '↩️ Back to Ace',
    body: "Great! Now let's add Ace to your cart. We've jumped back to A — tap anywhere to continue.",
    highlight: true,
    remeasureDelay: 700,
    onEnter: (a) => {
      a.collapseAce();
      setTimeout(() => {
        const aBtn = Array.from(document.querySelectorAll('.alphabet-btn-top'))
          .find(el => el.textContent?.trim() === 'A') as HTMLElement | undefined;
        if (aBtn) aBtn.click();
      }, 100);
    },
  },
  {
    id: 'cart-add',
    target: '[data-villager="Ace"] .cart-arrow-btn',
    placement: 'top',
    title: '🛒 Add Ace to Cart',
    body: "Press → on Ace's card to add them to your cart!",
    highlight: true,
    waitForAction: true,
    pollForCart: true,
    onEnter: (a) => {
      a.collapseAce();
      setTimeout(() => {
        const aBtn = Array.from(document.querySelectorAll('.alphabet-btn-top'))
          .find(el => el.textContent?.trim() === 'A') as HTMLElement | undefined;
        if (aBtn) aBtn.click();
      }, 100);
    },
  },
  {
    id: 'cart-pill',
    target: '.cart-pill',
    placement: 'top',
    title: '🛒 Open Your Cart',
    body: "Ace is in your cart! Tap the cart pill to open it.",
    highlight: true,
    waitForAction: true,
    pollForCartOpen: true,
  },
  {
    id: 'checkout-plot',
    target: '.checkout-modal',
    placement: 'top',
    title: '💸 Checkout — Send Your Request',
    body: "Tick the \"I have an available plot\" box for Ace, then tap Send Request!",
    highlight: true,
    waitForAction: true,
    pollForCheckoutSent: true,
    onEnter: (a) => { a.openCheckout(); },
    remeasureDelay: 300,
  },
  {
    id: 'done',
    placement: 'center',
    title: '🎉 You\'re all set!',
    body: "Your trade request was sent and will be cleaned up. Tap ☰ Menu to go to Trades or Profile. Happy trading! 🏝️",
    actionLabel: 'Start exploring →',
  },
];

interface SpotlightRect { top: number; left: number; width: number; height: number; }

function getRect(selector: string): SpotlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

interface TutorialProps {
  onComplete: () => void;
  onCancelTrade: () => void;
  isMobile: boolean;
  actions: TutorialActions;
}

const PAD = 10;
const TOOLTIP_H_EST = 240;

export default function Tutorial({ onComplete, onCancelTrade, isMobile, actions }: TutorialProps) {
  const steps = isMobile ? MOBILE_STEPS : WEB_STEPS;
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const measureTimers = useRef<number[]>([]);

  const step = steps[stepIdx];

  // Lock body scroll while tutorial is active
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  const measureTarget = useCallback((targetOverride?: string) => {
    const target = targetOverride || step.target;
    if (!target) { setRect(null); return; }
    measureTimers.current.forEach(clearTimeout);
    measureTimers.current = [];
    let attempts = 0;
    const tryMeasure = () => {
      const el = document.querySelector(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const t = window.setTimeout(() => {
          const r = getRect(target);
          if (r) {
            setRect(r);
          } else if (attempts < 12) {
            attempts++;
            const t2 = window.setTimeout(tryMeasure, 100);
            measureTimers.current.push(t2);
          } else {
            setRect(null);
          }
        }, 120);
        measureTimers.current.push(t);
      } else if (attempts < 12) {
        attempts++;
        const t = window.setTimeout(tryMeasure, 100);
        measureTimers.current.push(t);
      } else {
        setRect(null);
      }
    };
    tryMeasure();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.setAttribute('data-tutorial-step', step.id);
    return () => document.body.removeAttribute('data-tutorial-step');
  }, [step.id]);

  useEffect(() => {
    setVisible(false);
    setRect(null);
    if (step.onEnter) step.onEnter(actions);
    const delay = step.remeasureDelay ? step.remeasureDelay + 100 : 300;
    const t = window.setTimeout(() => {
      measureTarget();
      setVisible(true);
    }, delay);
    return () => clearTimeout(t);
  }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measure on resize
  useEffect(() => {
    const handler = () => { if (step.target) measureTarget(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measureTarget, step.target]);

  // Poll for action-required steps
  useEffect(() => {
    if (!step.waitForAction) return;
    const interval = window.setInterval(() => {
      if (step.pollForExpanded) {
        if (document.querySelector('[data-villager="Ace"] .card-expanded')) {
          window.clearInterval(interval);
          setStepIdx(i => i + 1);
        }
      } else if (step.pollFor) {
        // For alphabet: detect any alphabet letter click by checking scroll position near M
        const mEl = document.querySelector('[data-letter="M"], #letter-M, [data-alpha="M"]');
        if (mEl) {
          const rect = mEl.getBoundingClientRect();
          // M was clicked if the viewport scrolled near M section
          const mSection = document.querySelector('[data-section="M"], #section-M, .grid-letter-divider[data-letter="M"]');
          if (mSection) {
            const sr = mSection.getBoundingClientRect();
            if (sr.top >= -50 && sr.top <= window.innerHeight * 0.5) {
              window.clearInterval(interval);
              setStepIdx(i => i + 1);
            }
          } else if (rect.top >= 0) {
            // fallback: just advance after any alphabet letter click detected via DOM mutation
          }
        }
      } else if (step.pollForCart) {
        // DOM-based: Ace card gets .selected class when in basket
        if (document.querySelector('[data-villager="Ace"].selected')) {
          window.clearInterval(interval);
          setStepIdx(i => i + 1);
        }
      } else if (step.pollForCartOpen) {
        // DOM-based: cart panel or checkout overlay visible
        if (document.querySelector('.cart-panel') || document.querySelector('.checkout-modal')) {
          window.clearInterval(interval);
          setStepIdx(i => i + 1);
        }
      } else if (step.pollForCheckoutSent) {
        // DOM-based: checkout-success shown means request was sent
        if (document.querySelector('.checkout-success')) {
          window.clearInterval(interval);
          setStepIdx(i => i + 1);
        }
      }
    }, 300);
    return () => window.clearInterval(interval);
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // For alphabet step, also listen to clicks on alphabet buttons
  useEffect(() => {
    if (step.id !== 'alphabet') return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.alphabet-nav-top')) {
        window.setTimeout(() => setStepIdx(i => i + 1), 400);
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    if (stepIdx === steps.length - 1) {
      localStorage.setItem(TUTORIAL_DONE_KEY, '1');
      onComplete();
    } else {
      setStepIdx(i => i + 1);
    }
  };

  const skip = () => {
    onCancelTrade();
    localStorage.setItem(TUTORIAL_DONE_KEY, '1');
    onComplete();
  };

  // Compute tooltip position — always centered on the highlighted element
  const getTooltipStyle = (): React.CSSProperties => {
    if (!rect || step.placement === 'center') {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: isMobile ? 'min(92vw, 360px)' : '400px',
        width: isMobile ? 'min(92vw, 360px)' : '400px',
      };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const TW = isMobile ? Math.min(vw * 0.9, 340) : 370;
    const placement = step.placement || 'bottom';
    // Centre the tooltip horizontally on the spotlight
    const centreX = rect.left + rect.width / 2;
    const clampedLeft = Math.max(8, Math.min(centreX - TW / 2, vw - TW - 8));

    if (placement === 'bottom') {
      const spottomEdge = rect.top + rect.height + PAD;
      const top = spottomEdge + 8;
      // If tooltip would overflow bottom, flip to top
      if (top + TOOLTIP_H_EST > vh - 8) {
        return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
      }
      return { position: 'fixed', top, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (placement === 'top') {
      const bottom = vh - rect.top + PAD + 8;
      return { position: 'fixed', bottom, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (placement === 'right') {
      const left = Math.min(rect.left + rect.width + PAD + 8, vw - TW - 8);
      const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
      return { position: 'fixed', top, left, width: TW, maxWidth: TW };
    }
    // left
    const left = Math.max(8, rect.left - TW - PAD - 8);
    const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
    return { position: 'fixed', top, left, width: TW, maxWidth: TW };
  };

  const spotlightStyle: React.CSSProperties = rect ? {
    position: 'fixed',
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 14,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    border: '1.5px solid rgba(255,140,30,0.7)',
    animation: 'tutorial-glow-pulse 1.8s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 200001,
    transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1)',
  } : {
    position: 'fixed',
    top: 0, left: 0, width: 0, height: 0,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    pointerEvents: 'none',
    zIndex: 200001,
  };

  // Four blocking strips that surround the spotlight hole, leaving only the target clickable
  const renderActionBlocker = () => {
    const blockEvt = (e: React.MouseEvent | React.TouchEvent) => { e.stopPropagation(); e.preventDefault(); };
    if (!step.waitForAction || !rect) {
      return (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200000 }}
          onClick={e => { e.stopPropagation(); advance(); }}
          onTouchStart={e => { e.stopPropagation(); advance(); }}
          onTouchMove={e => e.preventDefault()}
        />
      );
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const ht = rect.top - PAD;
    const hl = rect.left - PAD;
    const hb = rect.top + rect.height + PAD;
    const hr = rect.left + rect.width + PAD;
    const s: React.CSSProperties = { position: 'fixed', zIndex: 200000, background: 'transparent' };
    return (
      <>
        <div style={{ ...s, top: 0, left: 0, width: vw, height: Math.max(0, ht) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: hb, left: 0, width: vw, height: Math.max(0, vh - hb) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: 0, width: Math.max(0, hl), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: hr, width: Math.max(0, vw - hr), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
      </>
    );
  };

  return (
    <div
      className={`tutorial-root${visible ? ' visible' : ''}`}
      data-tutorial-step={step.id}
      style={{ position: 'fixed', inset: 0, zIndex: 200000, pointerEvents: step.waitForAction ? 'none' : 'all' }}
    >
      {renderActionBlocker()}

      {/* Dim overlay — full screen when no spotlight */}
      {!rect && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.76)', zIndex: 200002, pointerEvents: 'none' }} />
      )}

      {/* Spotlight cutout (uses box-shadow to darken everything outside) */}
      {rect && <div style={spotlightStyle} />}

      {/* Tooltip card */}
      <div
        className="tutorial-card"
        style={{ ...getTooltipStyle(), pointerEvents: 'all', zIndex: 200002, transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), bottom 0.3s cubic-bezier(0.4,0,0.2,1)' }}
        onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
        onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
      >
        {/* Progress dots */}
        <div className="tutorial-dots">
          {steps.map((_, i) => (
            <span key={i} className={`tutorial-dot ${i === stepIdx ? 'active' : i < stepIdx ? 'done' : ''}`} />
          ))}
        </div>

        <h3 className="tutorial-title">{step.title}</h3>
        <p className="tutorial-body" style={{ whiteSpace: 'pre-line' }}>{step.body}</p>

        <div className="tutorial-actions">
          <button className="tutorial-skip" onClick={skip}>Skip</button>
          {/* Only show Finish button on final step; never show Next on waitForAction steps */}
          {(!step.waitForAction) && stepIdx === steps.length - 1 && (
            <button className="tutorial-next" onClick={advance}>
              {step.actionLabel || 'Finish'}
            </button>
          )}
          {step.actionLabel && !step.waitForAction && stepIdx < steps.length - 1 && (
            <button className="tutorial-next" onClick={advance}>{step.actionLabel}</button>
          )}
          {/* Tap hint for non-action info steps */}
          {!step.waitForAction && stepIdx < steps.length - 1 && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>tap anywhere to continue</span>
          )}
        </div>

        {/* Step counter */}
        <div className="tutorial-counter">{stepIdx + 1} / {steps.length}</div>
      </div>
    </div>
  );
}

export function shouldShowTutorial(): boolean {
  return !localStorage.getItem(TUTORIAL_DONE_KEY);
}

export async function cancelTutorialTrade(userId: string): Promise<void> {
  await supabase
    .from('trade_requests')
    .delete()
    .eq('requester_id', userId)
    .eq('status', 'open');
}
